import { Link, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Loader2 } from "lucide-react";
import { StoreHeader } from "../components/StoreHeader";
import { formatINR } from "../lib/format";
import { VinciPulsePoint } from "../components/VinciPulsePoint";
import { useAuth } from "../context/AuthContext";

export default function OrderSuccess() {
  const { state } = useLocation();
  const [params] = useSearchParams();
  const { api } = useAuth();
  const oid = params.get("oid");
  const [fetched, setFetched] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!state?.order && oid) {
      api.get(`/orders/${oid}/detail`).then((r) => setFetched(r.data.order)).catch(() => setFailed(true));
    }
  }, [api, oid, state]);

  if (!state?.order && oid && !fetched && !failed) {
    return <div className="min-h-screen bg-[#070707] grid place-items-center" data-testid="order-success-loading"><Loader2 className="animate-spin text-[#D8B23E]" /></div>;
  }
  const order = state?.order || fetched;
  if (!order) return <Navigate to="/shop" replace />;
  const pending = state?.pending ?? (order.payment_status !== "paid");

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]">
      <StoreHeader />
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} className="mx-auto w-20 h-20 grid place-items-center rounded-full" style={{ background: pending ? "#D8B23E22" : "#2FB99922" }}>
          {pending ? <Clock size={36} className="text-[#D8B23E]" /> : <CheckCircle2 size={40} className="text-[#2FB999]" />}
        </motion.div>
        <h1 className="font-serif text-4xl md:text-5xl mt-8">{pending ? "Order placed" : "Payment successful"}</h1>
        <p className="font-body text-[#8A8A82] mt-4 max-w-md mx-auto leading-relaxed">
          {pending
            ? "Your order is recorded. Our trade desk will send you a secure payment link to complete the purchase."
            : "Thank you — your order is confirmed. Our team will coordinate dispatch and share tracking details."}
        </p>

        <div className="mt-10 text-left rounded-sm border border-white/10 p-8">
          <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-4">
            <span className="overline text-[#8A8A82]">Order ID</span>
            <span className="font-grotesk text-sm">{order.id.slice(0, 8).toUpperCase()}</span>
          </div>
          {order.items.map((i) => (
            <div key={i.product_id} className="flex items-center justify-between py-2 font-body text-sm">
              <span className="text-[#C9C9C2]">{i.quantity} × {i.name}</span>
              <span>{formatINR(i.amount)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-4 mt-3 border-t border-white/10">
            <span className="font-serif text-lg">Total</span>
            <span className="font-serif text-2xl">{formatINR(order.total)}</span>
          </div>
        </div>

        <div className="flex gap-3 justify-center mt-10 flex-wrap">
          <VinciPulsePoint orderId={order.id} orderRef={order.id?.slice(0, 8).toUpperCase()} />
          <Link to="/account" className="rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">View my orders</Link>
          <Link to="/shop" className="rounded-full border border-white/20 px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}
